import { Line3, Points, Vector3 } from 'three';
import _ from 'underscore';

import { isDefined } from '../../../../../../utils/variables';
import { CoordinateManager } from '../../../../../coordinate-manager';
import { DesignNode, RevertChange } from '../../../../../elements/design/design-node/design-node';
import {
  getConicParameter,
  getConicPoint,
} from '../../../../../elements/design/design-node/utils/conic';
import { FaceId } from '../../../../../elements/design/design-node/utils/face-node';
import {
  LineSegmentId,
  LineSegmentNode,
} from '../../../../../elements/design/design-node/utils/line-segment-node';
import { PointId } from '../../../../../elements/design/design-node/utils/point-node';
import { AttributeData, AttributeId } from '../../../../../elements/design/skymap-extras';
import { Coordinates } from '../../../../../utilities/math-helpers';
import { Pick } from '../../../../../utilities/pick';
import { DesignTool } from '../../../../design-tool';
import { setAttributesOnSelection } from './attributes';
import { breakLineSegment } from './break-line';
import { getLineSegmentConic } from './curve';
import { repositionDesign } from './re-position';

/**
 * How far from a line segment (meters) a point is still on it, when finding the part of a broken
 * line segment that a point is on.
 */
const onLineTolerance = 1e-6;

export type InterpolationSnapMode = 'pointCloud' | 'assets' | 'height' | 'relativeHeight';

/**
 * Gets the line segments to break at the position. A line segment that has been broken since it
 * was intersected (e.g. by a point placed on it before) is replaced by its part at the position:
 * a line segment through the position that isn't one of the others.
 *
 * @param pointId The point at the position, if there is one: the line segments that end there are
 * not broken by it.
 */
function resolveLineSegments(
  node: DesignNode,
  ids: (LineSegmentId | undefined)[],
  position: Vector3,
  pointId?: PointId,
): LineSegmentNode[] {
  const endsAtPoint = (id: LineSegmentId) =>
    isDefined(pointId) && node.getLineSegmentById(id).values.includes(pointId);
  const lineSegments = node.lineSegments.filter((id) => !endsAtPoint(id));
  const defined = _.uniq(ids.filter(isDefined));
  const resolved = defined.filter((id) => lineSegments.includes(id));

  const isAtPosition = (id: LineSegmentId) => {
    const [start, end] = node.getVerticesByLineSegmentId(id, 'local');
    const conic = getLineSegmentConic(node, id, 'local');
    const closest = conic
      ? getConicPoint(conic, getConicParameter(conic, position))
      : new Line3(start, end).closestPointToPoint(position, true, new Vector3());

    return closest.distanceTo(position) <= onLineTolerance;
  };

  for (let i = resolved.length; i < defined.length; i++) {
    const part = lineSegments.find((id) => !resolved.includes(id) && isAtPosition(id));

    if (isDefined(part)) {
      resolved.push(part);
    }
  }

  return resolved.map((id) => node.getLineSegmentById(id));
}

/**
 * Creates a point, or uses the intersected point. A new point on an intersected line segment breaks
 * it, and also the line segment it crosses there (see CrossingIntersection). The intersected point
 * breaks the line segment it is on, where it is the end of a line that ends on that one (a T).
 */
export function createNewPoint(
  tool: DesignTool,
  coordinates: Coordinates,
  intersectedLineSegmentId?: LineSegmentId,
  interSectedPointId?: PointId,
  activeAttributes = new Map<AttributeId, AttributeData>(),
  crossedLineSegmentId?: LineSegmentId,
): { id: PointId; revertChanges: RevertChange[] } {
  const changes: RevertChange[] = [];

  if (isDefined(interSectedPointId)) {
    changes.unshift(
      ...setAttributesOnSelection(
        tool,
        {
          points: [interSectedPointId],
          lineSegments: [],
          faces: [],
        },
        activeAttributes,
      ),
    );

    // Where a line of the design ends on another one (a T), the point breaks the other line.
    const node = tool.asset.designNode;
    const position = node.getVertexByPointId(interSectedPointId, 'local');
    const [crossedLine] = resolveLineSegments(
      node,
      [crossedLineSegmentId],
      position,
      interSectedPointId,
    );

    if (crossedLine) {
      changes.unshift(...breakLineSegment(node, interSectedPointId, crossedLine));
    }

    return {
      id: interSectedPointId,
      revertChanges: changes,
    };
  }

  if (tool.asset.designNode.pointNodeCount === 0) {
    changes.unshift(repositionDesign(tool, coordinates.easting, coordinates.northing));
  }

  const node = tool.asset.designNode;
  const worldPosition = CoordinateManager.getInstance().projectCoordsToLocal(coordinates);
  const position = node.getLocalPosition(worldPosition);
  const lines = resolveLineSegments(
    node,
    [intersectedLineSegmentId, crossedLineSegmentId],
    position,
  );
  const result = node.addPoint(position, activeAttributes ?? lines[0]?.attributes);

  changes.unshift(result);

  for (const line of lines) {
    changes.unshift(...breakLineSegment(node, result.id, line));
  }

  return { id: result.id, revertChanges: changes };
}

export function removePoint(node: DesignNode, removePointId: PointId): RevertChange[] {
  const point = node.getPointById(removePointId);

  if (point.segments.length) {
    const segment = node.getLineSegmentById(_.min(point.segments) as LineSegmentId);
    return combinePoints(
      node,
      segment.values[0] === removePointId ? segment.values[1] : segment.values[0],
      removePointId,
    );
  }

  return [node.removePoint(removePointId)];
}

/**
 * Combines a point with another
 */
export function combinePoints(
  node: DesignNode,
  keptPointId: PointId,
  removedPointId: PointId,
): RevertChange[] {
  const keep = node.getPointById(keptPointId);
  const remove = node.getPointById(removedPointId);
  const changes: RevertChange[] = [];

  const newFaces: FaceId[] = [];
  const newLinesegments: LineSegmentId[] = [];

  for (const id of remove.segments) {
    const segment = node.getLineSegmentById(id);

    const newValues = segment.values.map((x) => (x === removedPointId ? keptPointId : x)) as [
      PointId,
      PointId,
    ];

    if (
      segment.values.includes(keptPointId) ||
      isDefined(node.findLineSegmentByPointIds(newValues[0], newValues[1]))
    ) {
      changes.unshift(node.removeLineSegment(id));
    } else {
      newLinesegments.push(id);
      changes.unshift(
        node.upsertLineSegment({
          id,
          values: newValues,
        }),
      );
    }
  }

  for (const id of remove.faces) {
    const face = node.getFaceById(id);

    const newValues = face.values.map((x) => (x === removedPointId ? keptPointId : x)) as [
      PointId,
      PointId,
      PointId,
    ];

    if (
      face.values.includes(keptPointId) ||
      isDefined(node.findFaceByPointIds(newValues[0], newValues[1], newValues[2]))
    ) {
      changes.unshift(node.removeFace(id));
    } else {
      newFaces.push(id);
      changes.unshift(
        node.upsertFace({
          id,
          values: newValues,
        }),
      );
    }
  }

  // The circles around the removed point are around the kept point.
  for (const id of node.getSeams(removedPointId)) {
    changes.unshift(node.upsertPoint({ id, seamOf: keptPointId }));
  }

  changes.unshift(node.removePoint(removedPointId));

  changes.unshift(
    node.upsertPoint({
      id: keptPointId,
      faces: _.uniq([...keep.faces, ...newFaces]),
      segments: _.uniq([...keep.segments, ...newLinesegments]),
      attributes: keep.attributes,
      vertex: keep.vertex,
    }),
  );

  return changes;
}

export function setToolEditIntersectionToPoint(tool: DesignTool, pointId: PointId) {
  const index = tool.asset.designNode.findIndexByPointId(pointId);

  if (!isDefined(index)) {
    throw new Error('Unable to set intersection. Index not found.');
  }

  (tool.currentPoint = tool.asset.designNode.getVertexByPointId(pointId, 'world')),
    (tool.intersection = {
      object: tool.asset.designNode.data.points,
      point: tool.currentPoint.clone(),
      index,
      distance: 0,
    });
}

/**
 * Gets the point height based on snap mode.
 * Accepts the world position and returns local height for the design asset.
 *
 * If the snap mode is pointcloud it will intersect towards the provided point clouds.
 * If the snap mode is assets it will snap towards visible assets in the scene to find the height.
 */
export async function getPointHeight(
  tool: DesignTool,
  worldPosition: Vector3,
  snapMode: InterpolationSnapMode,
  pointCloudChunks: Points[],
  value: number,
): Promise<number | undefined> {
  return new Promise((resolve, _reject) => {
    /**
     * Computes the intersection within a setTimeout block so that it doesn't block the main thread.
     * This is done so that the UI will still respond and progress bar can be updated.
     */
    setTimeout(() => {
      let pickedPoint: Vector3 | undefined;
      switch (snapMode) {
        case 'pointCloud':
        case 'assets':
          pickedPoint = Pick.instance.onRay(
            tool.asset,
            new Vector3(worldPosition.x, worldPosition.y, worldPosition.z + 800),
            worldPosition,
            snapMode === 'assets' ? 'asset' : 'pointcloud',
            pointCloudChunks,
          );

          // Skip point if not found
          if (!pickedPoint) {
            resolve(undefined);
            return;
          }

          resolve(tool.asset.designNode.getLocalPosition(pickedPoint).z);
          break;
        case 'height':
          resolve(
            tool.asset.designNode.getLocalPosition(
              new Vector3(0, 0, value - CoordinateManager.getInstance().getMAS()),
            ).z,
          );
          break;
        case 'relativeHeight':
          resolve(tool.asset.designNode.getLocalPosition(worldPosition).z + value);
          break;
      }

      resolve(undefined);
    });
  });
}
